export type TypographyScaleKey = 'titles' | 'subtitles' | 'body' | 'labels' | 'captions';
export type TypographyScale = Record<TypographyScaleKey, number>;

export const TYPOGRAPHY_SCALE_PREFERENCE_KEY = 'ui_typography_scale';
export const TYPOGRAPHY_SCALE_STORAGE_KEY = 'callhub:typography-scale:v1';
export const MIN_TYPOGRAPHY_STEP = -4;
export const MAX_TYPOGRAPHY_STEP = 4;

const TYPOGRAPHY_STEP_MULTIPLIER = 0.06;
const TYPOGRAPHY_KEYS: TypographyScaleKey[] = ['titles', 'subtitles', 'body', 'labels', 'captions'];

export const DEFAULT_TYPOGRAPHY_SCALE: TypographyScale = {
  titles: 0,
  body: 0,
  subtitles: 0,
  labels: 0,
  captions: 0,
};

function clampStep(step: number): number {
  return Math.min(MAX_TYPOGRAPHY_STEP, Math.max(MIN_TYPOGRAPHY_STEP, step));
}

function normalizeStep(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  return clampStep(Math.round(value));
}

function toMultiplier(step: number): string {
  return (1 + step * TYPOGRAPHY_STEP_MULTIPLIER).toFixed(3);
}

export function normalizeTypographyScale(value: unknown): TypographyScale {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { ...DEFAULT_TYPOGRAPHY_SCALE };
  }

  const raw = value as Record<string, unknown>;
  const hasLegacyKeys = raw.headers !== undefined || raw.subheaders !== undefined;
  const hasNewKeys =
    raw.subtitles !== undefined || raw.labels !== undefined || raw.captions !== undefined;
  const legacyBody = raw.body;
  const bodyValue =
    // In legacy configs, `titles` controlled text-base, which now maps to `body`.
    (hasLegacyKeys && !hasNewKeys ? raw.titles : undefined) ?? raw.body ?? raw.titles;

  return {
    titles: normalizeStep(raw.headers ?? raw.titles),
    subtitles: normalizeStep(raw.subtitles ?? raw.subheaders),
    body: normalizeStep(bodyValue),
    labels: normalizeStep(raw.labels ?? legacyBody),
    captions: normalizeStep(raw.captions ?? legacyBody),
  };
}

export function adjustTypographyScale(
  current: TypographyScale,
  key: TypographyScaleKey,
  delta: number,
): TypographyScale {
  const normalizedCurrent = normalizeTypographyScale(current);
  const normalizedDelta = Number.isFinite(delta) ? Math.round(delta) : 0;
  return {
    ...normalizedCurrent,
    [key]: clampStep(normalizedCurrent[key] + normalizedDelta),
  };
}

export function getStoredTypographyScale(): TypographyScale {
  if (typeof window === 'undefined') return { ...DEFAULT_TYPOGRAPHY_SCALE };

  const raw = window.localStorage.getItem(TYPOGRAPHY_SCALE_STORAGE_KEY);
  if (!raw) return { ...DEFAULT_TYPOGRAPHY_SCALE };

  try {
    return normalizeTypographyScale(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_TYPOGRAPHY_SCALE };
  }
}

export function setStoredTypographyScale(scale: TypographyScale) {
  if (typeof window === 'undefined') return;
  const normalized = normalizeTypographyScale(scale);
  window.localStorage.setItem(TYPOGRAPHY_SCALE_STORAGE_KEY, JSON.stringify(normalized));
}

export function applyTypographyScale(scale: TypographyScale) {
  if (typeof document === 'undefined') return;

  const normalized = normalizeTypographyScale(scale);
  const root = document.documentElement;
  const multiplierByKey: Record<TypographyScaleKey, string> = {
    titles: toMultiplier(normalized.titles),
    body: toMultiplier(normalized.body),
    subtitles: toMultiplier(normalized.subtitles),
    labels: toMultiplier(normalized.labels),
    captions: toMultiplier(normalized.captions),
  };

  for (const key of TYPOGRAPHY_KEYS) {
    root.style.setProperty(`--type-scale-${key}`, multiplierByKey[key]);
  }
}

export function initializeDocumentTypography() {
  const stored = getStoredTypographyScale();
  applyTypographyScale(stored);
  return stored;
}
